import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import {
  addMonths,
  endOfDay,
  endOfToday,
  startOfDay,
  startOfToday,
  subMonths,
} from "date-fns"
import { TimestampRange as TimestampRangePB } from "gen/proto/timestamprange/models_pb"
import { ArrowRight, Calendar } from "lucide-react"
import { useRef, useState } from "react"
import {
  DisabledMode,
  DisabledModeT,
} from "shared-ts/hooks/useFilter/types/filter.types"
import {
  FullInterval,
  PartialInterval,
  isFullInterval,
  newFullIntervalFromTimestampRange,
} from "shared-ts/libs/dates/interval"
import {
  Preset,
  listDateRangePresets,
  matchingPreset,
} from "shared-ts/libs/dates/presets"
import { TimestampRange } from "shared-ts/libs/dates/timestampRange"

import { TimestampRangePickerCalendar } from "./TimestampRangePickerCalendar"
import { TimestampRangePickerPresetSidebar } from "./TimestampRangePickerPresetSidebar"
import { classNames } from "../../../lib/classNames"
import { TextDateInput } from "../../base/input/text/TextDateInput"
import { Icon, IconSizes } from "../../Icon"

interface TimestampRangePickerProps {
  timestampRange: TimestampRangePB | null
  onChangeTimestampRange: (range: TimestampRangePB | null) => void
  showSelectedValueInButton?: boolean
  buttonSize?: "small" | "large"
  buttonClassName?: string
  panelClassName?: string
  showAllTimePreset?: boolean
  popoverButtonTestId?: string
  disabledMode?: DisabledModeT
  // Portals the panel above the modal so it isn't clipped by the modal's
  // scroll container.
  isInModal?: boolean
}

export interface UseIntervalPickerProps {
  publishedInterval: FullInterval | null
  onChangePublishedInterval: (range: FullInterval | null) => void
  onPresetSelect: (preset: Preset) => void
  disabledMode?: DisabledModeT
}

export function useIntervalPicker(props: UseIntervalPickerProps) {
  const {
    publishedInterval: publishedRange,
    onChangePublishedInterval: onChangeTimestampRange,
    disabledMode = DisabledMode.Future,
    onPresetSelect,
  } = props

  const [draftRange, setDraftRange] = useState<PartialInterval | null>(null)
  const displayRange = draftRange || publishedRange

  const today = startOfToday()
  const [monthDisplayed, setMonthDisplayed] = useState<Date>(today)

  const fromInputRef = useRef<HTMLInputElement>(null)
  const toInputRef = useRef<HTMLInputElement>(null)

  const [currentDateField, setCurrentDateField] = useState<"from" | "to">(
    "from"
  )

  function setSelection(range: PartialInterval | null) {
    setDraftRange(range)

    if (range === null || range.isEmpty()) {
      onChangeTimestampRange(null)
    } else if (isFullInterval(range)) {
      onChangeTimestampRange(range)
    }
  }

  function onDelete() {
    setSelection(null)
  }

  function onSelectPreset(preset: Preset) {
    onPresetSelect?.(preset)
  }

  function onChangeFromDate(day: Date | null) {
    const newDraft = new PartialInterval({
      start: day ? startOfDay(day) : undefined,
      end: displayRange?.end,
    })
    setSelection(newDraft)
    toInputRef.current?.focus()
  }

  function onChangeToDate(day: Date | null) {
    const newDraft = new PartialInterval({
      start: displayRange?.start,
      end: day ? endOfDay(day) : undefined,
    })
    setSelection(newDraft)
  }

  function onDateClicked(day: Date) {
    if (currentDateField === "from") {
      onChangeFromDate(day)
    } else {
      onChangeToDate(day)
    }
  }

  const startInputProps = {
    ref: fromInputRef,
    date: displayRange?.start ?? null,
    onFocus: () => {
      setCurrentDateField("from")
      setMonthDisplayed(displayRange?.start ?? today)
    },
    onBlur: () => toInputRef.current?.focus(),
    defaultRefDate: startOfToday(),
    disabledMode: disabledMode,
    placeholder: "From",
    autoFocus: true,
    onChangeDate: onChangeFromDate,
  }

  const endInputProps = {
    ref: toInputRef,
    date: displayRange?.end ?? null,
    onFocus: () => {
      setCurrentDateField("to")
      setMonthDisplayed(monthDisplayed ?? displayRange?.end ?? today)
    },
    onBlur: () => fromInputRef.current?.focus(),
    defaultRefDate: monthDisplayed ?? endOfToday(),
    disabledMode: disabledMode,
    placeholder: "To",
    onChangeDate: onChangeToDate,
  }

  return {
    monthDisplayed,
    onClickPreviousMonth: () => setMonthDisplayed(subMonths(monthDisplayed, 1)),
    onClickNextMonth: () => setMonthDisplayed(addMonths(monthDisplayed, 1)),
    currentDateField,
    displayRange,
    onDateClicked,
    onSelectPreset,
    startInputProps,
    endInputProps,
    onDelete,
    disabledMode,
  }
}

export function TimestampRangePicker(props: TimestampRangePickerProps) {
  const {
    timestampRange,
    onChangeTimestampRange,
    showSelectedValueInButton,
    buttonClassName,
    panelClassName,
    showAllTimePreset,
    disabledMode,
    buttonSize = "large",
    isInModal,
  } = props

  const publishedRange = newFullIntervalFromTimestampRange(timestampRange)

  function handleChangePublishedInterval(range: PartialInterval | null) {
    onChangeTimestampRange(range?.toTimestampRangePB() ?? null)
  }

  const presetsToDisplay = listDateRangePresets(!!showAllTimePreset)

  const selectedPreset = matchingPreset(presetsToDisplay, publishedRange)

  function buttonText(): string {
    if (!showSelectedValueInButton) return "Date"
    if (!publishedRange?.isValid()) {
      return showAllTimePreset ? "All time" : "Date"
    }
    if (selectedPreset) {
      return selectedPreset.displayText
    }

    return TimestampRange.humanize(publishedRange.toTimestampRangePB())
  }

  return (
    <Popover className="relative">
      <PopoverButton
        data-testid="timestamp-range-picker-button"
        className={classNames(
          "border-v2-border-default text-v2-foreground-default-weak focus:text-v2-foreground-default rounded-control text-body relative z-10 flex w-full items-center gap-x-2 border bg-white focus:z-20 focus:border-gray-300 dark:bg-gray-800 dark:focus:border-gray-400",
          buttonSize === "large" && "px-4 py-2",
          buttonSize === "small" && "h-7 px-2",
          buttonClassName
        )}
      >
        <Icon
          icon={Calendar}
          size={buttonSize === "large" ? IconSizes.Small : IconSizes.ExtraSmall}
          className="text-v2-foreground-default-weak"
        />
        {buttonText()}
      </PopoverButton>

      <PopoverPanel
        anchor={
          isInModal ? { to: "bottom end", gap: 4, padding: 4 } : undefined
        }
        className={classNames(
          isInModal ? "z-[60]" : "absolute right-0 z-40 mt-1",
          panelClassName
        )}
      >
        {({ close }) => (
          <TimestampRangePickerPanel
            publishedInterval={publishedRange}
            onChangePublishedInterval={(range) => {
              handleChangePublishedInterval(range)
            }}
            onPresetSelect={(preset) => {
              handleChangePublishedInterval(
                newFullIntervalFromTimestampRange(preset.toTimestampRangePB())
              )
              close()
            }}
            selectedPreset={selectedPreset}
            presetsToDisplay={presetsToDisplay}
            disabledMode={disabledMode}
          />
        )}
      </PopoverPanel>
    </Popover>
  )
}

interface TimestampRangePickerPanelProps extends UseIntervalPickerProps {
  selectedPreset: Preset | null
  presetsToDisplay: Preset[]
}

function TimestampRangePickerPanel({
  selectedPreset,
  presetsToDisplay,
  ...useIntervalPickerProps
}: TimestampRangePickerPanelProps) {
  const {
    monthDisplayed,
    onClickPreviousMonth,
    onClickNextMonth,
    currentDateField,
    displayRange,
    onDateClicked,
    onSelectPreset,
    startInputProps,
    endInputProps,
    disabledMode,
  } = useIntervalPicker(useIntervalPickerProps)

  return (
    <div className="bg-v2-background-elevation-overlay border-v2-border-default shadow-v2-overlay rounded-overlay flex flex-1 flex-row border p-4">
      <div className="min-w-84 flex flex-col gap-y-4 pr-4">
        <div className="border-b-v2-border-default flex flex-row items-center gap-x-2 border-b pb-4">
          <TextDateInput
            data-testid="timestamp-range-picker-from-input"
            {...startInputProps}
          />
          <Icon
            icon={ArrowRight}
            className=" text-v2-foreground-default h-7 w-7"
          />
          <TextDateInput
            data-testid="timestamp-range-picker-to-input"
            {...endInputProps}
          />
        </div>

        <TimestampRangePickerCalendar
          selection={displayRange}
          monthDisplayed={monthDisplayed}
          onClickPreviousMonth={onClickPreviousMonth}
          onClickNextMonth={onClickNextMonth}
          currentlySelecting={currentDateField}
          onDateClicked={onDateClicked}
          disabledMode={disabledMode}
        />
      </div>
      <TimestampRangePickerPresetSidebar
        selectedPreset={selectedPreset}
        onSelectPreset={onSelectPreset}
        presetsToDisplay={presetsToDisplay}
      />
    </div>
  )
}
